// checkbox.component.ts  (polished)
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  Output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-checkbox',
  standalone: true,
  imports: [CommonModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxComponent),
      multi: true,
    },
  ],
  templateUrl: './checkbox.component.html',
  styleUrl: './checkbox.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckboxComponent implements ControlValueAccessor {
  private cdr = inject(ChangeDetectorRef);

  /* ------------ public API ------------ */
  @Input() label?: string;
  @Input() isDisabled = false;
  @Output() valueChange = new EventEmitter<boolean>();

  /** Current value kept in sync with the FormControl */
  private _value = false;
  get value(): boolean {
    return this._value;
  }

  /* ------------ ControlValueAccessor hooks ------------ */
  private _onChange: (value: boolean) => void = () => {};
  private _onTouched: () => void = () => {};

  onInputChange(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this._emitValue(checked);
  }

  writeValue(val: boolean | null): void {
    const normalized = !!val;
    if (normalized !== this._value) {
      this._value = normalized;
      this.cdr.markForCheck(); // OnPush only
    }
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.cdr.markForCheck();
  }

  /** Public wrapper so the template can mark the control as touched */
  handleBlur(): void {
    this._onTouched();
  }

  /* ------------ helpers ------------ */
  private _emitValue(val: boolean): void {
    this.writeValue(val); // update local copy
    this._onChange(val); // notify Angular forms
    this.valueChange.emit(val); // notify consumers not using forms
  }
}
